import * as Y from 'yjs';
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';
import { HubConnection } from '@microsoft/signalr';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Minimal logger interface for YjsSignalRProvider.
 * Duck-typed to avoid coupling this utility library to Angular DI or @pcp/logging.
 * Callers pass `inject(PCP_LOGGER).createScoped('yjs')` which satisfies this shape.
 */
export interface YjsProviderLogger {
    debug(message: string, properties?: Record<string, unknown>): void;
    warn(message: string, properties?: Record<string, unknown>): void;
    error(message: string, error?: Error, properties?: Record<string, unknown>): void;
}

/** No-op logger used when no logger is provided */
const NOOP_LOGGER: YjsProviderLogger = {
    debug: () => {},
    warn: () => {},
    error: () => {},
};

/**
 * YjsSignalRProvider - Transport layer for Yjs over SignalR (y-redis style)
 *
 * Connects a Y.Doc to SignalR hub for real-time synchronization:
 * - LOCAL → REMOTE: Broadcasts local Y.Doc updates to all clients via SignalR (all clients)
 * - REMOTE → LOCAL: Applies incoming SignalR updates to local Y.Doc
 * - STATE SYNC: Late joiners get state via HTTP REST endpoint (not SignalR)
 *
 * Architecture (y-redis style):
 * - All clients (Teacher, Beamer, Students) can broadcast Yjs updates
 * - All clients receive and apply updates from others
 * - Yjs CRDT handles conflict resolution automatically (last-writer-wins per property)
 * - **Server (Redis) is the SINGLE source of truth** - not Teacher!
 * - Late joiners fetch state via HTTP, which returns merged state from Redis
 * - Server is stateless - Y.Doc created on-demand for merging, then disposed
 *
 * State Sync Protocol:
 * - Initial state is fetched via `fetchInitialState` callback (HTTP GET)
 * - Incoming ReceiveYjsUpdate events are BUFFERED during the fetch
 * - After fetch completes: initial state applied, then buffered updates in order
 * - Subsequent ReceiveYjsUpdate events are applied normally (live stream)
 *
 * Binary Protocol:
 * - Uses MessagePack protocol for efficient binary serialization
 * - Yjs updates are sent as native Uint8Array (no Base64 encoding overhead)
 * - Server hub methods accept byte[] parameters directly
 *
 * Usage:
 * ```typescript
 * const yDoc = new Y.Doc();
 * const provider = new YjsSignalRProvider(documentId, yDoc, hubConnection, {
 *     fetchInitialState: async () => {
 *         const res = await fetch(`/api/yjs/${lessonId}/state?documentId=${documentId}`);
 *         return res.status === 200 ? new Uint8Array(await res.arrayBuffer()) : null;
 *     },
 * });
 * provider.connect(); // Start syncing
 * provider.disconnect(); // Stop syncing
 * ```
 */

export interface YjsSignalRProviderOptions {
    /**
     * If true, this client is the Teacher.
     * All clients (regardless of this flag) can send and receive Yjs updates.
     * Default: false
     */
    isTeacher?: boolean;

    /**
     * Observable that controls whether outgoing sync is enabled.
     * When emits false, local Y.Doc updates are not sent to the server.
     * When emits true, sync is enabled.
     *
     * Use this to pause sync when no lesson is active (server rejects messages anyway).
     * Provider subscribes internally and reacts to changes - no external coordination needed.
     */
    syncEnabled$?: Observable<boolean>;

    /**
     * HTTP fetch callback for initial Yjs state from the server.
     * Called on connect (when Y.Doc is empty) and on SignalR reconnection.
     *
     * The provider buffers incoming ReceiveYjsUpdate events during the fetch,
     * then applies the initial state followed by buffered updates in order.
     *
     * Returns Uint8Array (state exists) or null (no state / 204 No Content).
     */
    fetchInitialState?: () => Promise<Uint8Array | null>;

    /**
     * Logger instance for structured logging.
     * Pass `inject(PCP_LOGGER).createScoped('yjs')` from the calling service.
     * Falls back to no-op if not provided.
     */
    logger?: YjsProviderLogger;

    /**
     * Enable awareness transport (ephemeral state like live drag positions).
     * Requires SendAwarenessUpdate / ReceiveAwarenessUpdate hub methods on the server.
     * Default: false
     */
    enableAwareness?: boolean;

    /**
     * Whether this client may BROADCAST awareness. Receiving is always wired when
     * `enableAwareness` is true — a receive-only client still renders peers' cursors.
     *
     * Set this to the server's own authorization: the LessonHub gates
     * `SendAwarenessUpdate` behind `IsTeacherOrBeamerClient`, so a student or moderator
     * that sends is rejected with an "unauthorized" error. y-protocols renews any non-null
     * local state every 15s, so a non-writer would otherwise produce one rejected invoke
     * per client per 15s for the whole lesson.
     *
     * Deliberately separate from `syncEnabled$`: that gates the Y.Doc channel too, and a
     * dropped Y.Doc update is never retried.
     * Default: true (unchanged behaviour for callers that don't set it)
     */
    canSendAwareness?: boolean;

    /**
     * Observable gating awareness BROADCASTS on lesson lifecycle (e.g. "a lesson is running").
     * Receiving is unaffected, and the Y.Doc channel is unaffected — only ephemeral frames
     * are suppressed, and an ephemeral frame is disposable by construction.
     *
     * Use this to stop a client that outlives the lesson (teardown is bound to logout, not
     * lesson stop) from broadcasting into a lesson that has ended.
     * Default: always enabled.
     */
    awarenessSendEnabled$?: Observable<boolean>;
}

/**
 * Ensures the received data is a Uint8Array.
 * MessagePack may deserialize byte[] as ArrayBuffer or Uint8Array depending on version/config.
 */
function ensureUint8Array(data: Uint8Array | ArrayBuffer): Uint8Array {
    if (data instanceof Uint8Array) {
        return data;
    }
    return new Uint8Array(data);
}

export class YjsSignalRProvider {
    private isConnected = false;
    private syncEnabled = false;
    private updateHandler?: (update: Uint8Array, origin: unknown) => void;
    private readonly isTeacher: boolean;
    private readonly enableAwareness: boolean;
    private readonly canSendAwareness: boolean;
    private awarenessSendEnabled = true;
    private readonly log: YjsProviderLogger;

    // SignalR handler references for proper cleanup
    // MessagePack sends binary data as Uint8Array directly (no Base64)
    private receiveUpdateHandler?: (documentId: string, update: Uint8Array | ArrayBuffer) => void;
    private reconnectedHandler?: (connectionId?: string) => void;

    // Awareness: ephemeral state (cursors, live position during drag)
    // Separate from Y.Doc — no tombstones, no persistence, auto-expires on disconnect
    private readonly _awareness: Awareness;
    private awarenessUpdateHandler?: (changes: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => void;
    private receiveAwarenessUpdateHandler?: (documentId: string, update: Uint8Array | ArrayBuffer) => void;

    // Promise that resolves when initial state sync completes (or times out)
    private initialSyncPromise?: Promise<boolean>;
    private initialSyncResolve?: (hasState: boolean) => void;
    // Handle for the initial-sync safety timeout, so every settle path can cancel it.
    private initialSyncTimer?: ReturnType<typeof setTimeout>;

    // Subscription for reactive sync enabled control
    private syncEnabledSubscription?: Subscription;
    private awarenessSendEnabledSubscription?: Subscription;

    // Callback for fetching initial state via HTTP (injected by caller)
    private readonly fetchInitialStateFn?: () => Promise<Uint8Array | null>;

    // Buffer for ReceiveYjsUpdate events during fetchInitialState.
    // When non-null, incoming updates are buffered instead of applied immediately.
    private updateBuffer: Uint8Array[] | null = null;

    constructor(
        private readonly documentId: string,
        private readonly yDoc: Y.Doc,
        private readonly hubConnection: HubConnection,
        options: YjsSignalRProviderOptions = {}
    ) {
        this.isTeacher = options.isTeacher ?? false;
        this.enableAwareness = options.enableAwareness ?? false;
        this.canSendAwareness = options.canSendAwareness ?? true;
        this.fetchInitialStateFn = options.fetchInitialState;
        this.log = options.logger ?? NOOP_LOGGER;

        // Create Awareness instance for ephemeral state (lives outside Y.Doc)
        this._awareness = new Awareness(this.yDoc);

        // Subscribe to sync enabled state if provided (reactive control)
        if (options.syncEnabled$) {
            this.syncEnabledSubscription = options.syncEnabled$.pipe(
                distinctUntilChanged()
            ).subscribe(enabled => {
                this.syncEnabled = enabled;
                this.log.debug(`Sync ${enabled ? 'enabled' : 'disabled'}`);
            });
        } else {
            // No reactive control — sync is always enabled
            this.syncEnabled = true;
        }

        // Lifecycle gate for awareness broadcasts only (see awarenessSendEnabled$).
        if (options.awarenessSendEnabled$) {
            this.awarenessSendEnabledSubscription = options.awarenessSendEnabled$.pipe(
                distinctUntilChanged()
            ).subscribe(enabled => {
                this.awarenessSendEnabled = enabled;
                this.log.debug(`Awareness send ${enabled ? 'enabled' : 'disabled'}`);
            });
        }
    }

    /**
     * Awareness instance for ephemeral state (live position during drag, cursors, etc.)
     * Completely outside Y.Doc — no tombstones, no clock advancement, no Redis persistence.
     * Only useful when `enableAwareness` is true — otherwise transport is not wired.
     */
    get awareness(): Awareness {
        if (!this.enableAwareness) {
            this.log.warn('Accessing awareness but enableAwareness is false — transport is not wired');
        }
        return this._awareness;
    }

    /**
     * Connect provider and start syncing
     */
    connect(): void {
        if (this.isConnected) {
            this.log.warn('Already connected');
            return;
        }

        this.isConnected = true;

        // LOCAL → REMOTE: Broadcast local updates to all clients (bidirectional sync)
        // Both Teacher and Beamer/Students can send updates - Yjs CRDT handles conflict resolution
        this.updateHandler = (update: Uint8Array, origin: unknown) => {
            // Don't broadcast updates that came from remote (prevents loops)
            if (origin === this) {
                return;
            }

            // Skip sending if sync is disabled (e.g., no lesson active)
            if (!this.syncEnabled) {
                return;
            }

            this.log.debug('Broadcasting update via SendYjsUpdate', { size: update.byteLength });

            this.hubConnection
                .invoke('SendYjsUpdate', this.documentId, update)
                .catch((err) => {
                    this.log.error('Failed to send update via SendYjsUpdate', err as Error);
                });
        };

        this.yDoc.on('update', this.updateHandler);

        // REMOTE → LOCAL: Apply incoming updates from other clients
        // Data arrives as Uint8Array via MessagePack (native binary)
        this.receiveUpdateHandler = (documentId: string, update: Uint8Array | ArrayBuffer) => {
            if (documentId !== this.documentId) {
                return; // Ignore updates for other documents
            }

            const updateBytes = ensureUint8Array(update);

            // Buffer during initial state fetch to ensure correct ordering
            if (this.updateBuffer !== null) {
                this.updateBuffer.push(updateBytes);
                return;
            }

            // Apply remote update to local Y.Doc (origin = this prevents re-broadcast)
            Y.applyUpdate(this.yDoc, updateBytes, this);
        };
        this.hubConnection.on('ReceiveYjsUpdate', this.receiveUpdateHandler);

        // Request initial state if Y.Doc is empty
        // All clients (including Teacher) request state for restoration
        // - Teacher: Restores previous session if server has state (accidental close/reload)
        // - Beamer/Student: Gets current state as late joiner
        if (this.yDoc.store.clients.size === 0 && this.fetchInitialStateFn) {
            this.fetchAndApplyState().then(hasState => {
                if (this.initialSyncResolve) {
                    this.clearInitialSyncTimer();
                    this.initialSyncResolve(hasState);
                    this.initialSyncResolve = undefined;
                }
            });
        }

        // AWARENESS: Ephemeral state transport (live position during drag, cursors, etc.)
        // Awareness is completely separate from Y.Doc — no tombstones, no persistence.
        // Only enabled when the hub supports SendAwarenessUpdate / ReceiveAwarenessUpdate.
        if (this.enableAwareness) {
            // LOCAL → REMOTE: Broadcast awareness changes to all clients.
            // Only wired for clients the hub authorizes to send (canSendAwareness); a
            // receive-only client still renders peers' cursors via the handler below.
            if (this.canSendAwareness) {
                this.awarenessUpdateHandler = ({ added, updated, removed }, origin) => {
                    // Don't broadcast updates that came from remote (prevents loops)
                    if (origin === this) {
                        return;
                    }

                    // Skip sending if sync is disabled (applies to ALL awareness fields: position, steeringLock, etc.)
                    if (!this.syncEnabled) {
                        return;
                    }

                    // Skip sending once the lesson lifecycle gate has closed (e.g. lesson stopped
                    // on a client whose teardown is bound to logout).
                    if (!this.awarenessSendEnabled) {
                        return;
                    }

                    const changedClients = added.concat(updated).concat(removed);
                    const update = encodeAwarenessUpdate(this._awareness, changedClients);

                    // Fire-and-forget: a broadcast has no return value, so `.send` avoids the
                    // per-message Completion ack that `.invoke` costs. Transport failures still
                    // reject the returned promise, so error logging is unchanged.
                    this.hubConnection
                        .send('SendAwarenessUpdate', this.documentId, update)
                        .catch((err) => {
                            this.log.error('Failed to send awareness update', err as Error);
                        });
                };
                this._awareness.on('update', this.awarenessUpdateHandler);
            }

            // REMOTE → LOCAL: Apply incoming awareness updates from other clients
            this.receiveAwarenessUpdateHandler = (documentId: string, update: Uint8Array | ArrayBuffer) => {
                if (documentId !== this.documentId) {
                    return;
                }

                const updateBytes = ensureUint8Array(update);
                applyAwarenessUpdate(this._awareness, updateBytes, this);
            };
            this.hubConnection.on('ReceiveAwarenessUpdate', this.receiveAwarenessUpdateHandler);
        }

        // Handle SignalR reconnection - ALL clients request fresh state
        // Redis is the single source of truth - anyone could have sent updates while we were offline
        // (With bidirectional sync, Beamer can send updates while Teacher is temporarily disconnected)
        this.reconnectedHandler = () => {
            if (this.isConnected && this.fetchInitialStateFn) {
                this.log.debug('Reconnected, fetching state from server');
                this.fetchAndApplyState().catch(err => {
                    this.log.error('Failed to fetch state on reconnect', err as Error);
                });
            }
        };
        this.hubConnection.onreconnected(this.reconnectedHandler);
    }

    /**
     * Drains the update buffer: snapshots it, clears it, then applies all buffered updates.
     * Merges multiple updates into one before applying to fire the Yjs observer chain only once.
     */
    private drainUpdateBuffer(): void {
        const buffered = this.updateBuffer;
        this.updateBuffer = null;
        if (!buffered || buffered.length === 0) return;

        this.log.debug('Applying buffered updates', { count: buffered.length });
        if (buffered.length === 1) {
            Y.applyUpdate(this.yDoc, buffered[0], this);
        } else {
            Y.applyUpdate(this.yDoc, Y.mergeUpdates(buffered), this);
        }
    }

    /**
     * Fetch initial state via HTTP and apply it to Y.Doc.
     * Buffers incoming ReceiveYjsUpdate events during the fetch,
     * then applies initial state + buffered updates in correct order.
     *
     * @returns true if state was received and applied, false if no state or error
     */
    private async fetchAndApplyState(): Promise<boolean> {
        if (!this.fetchInitialStateFn) {
            this.log.warn('No fetchInitialState callback provided');
            return false;
        }

        // Start buffering incoming ReceiveYjsUpdate events
        this.updateBuffer = [];

        try {
            const state = await this.fetchInitialStateFn();

            // Guard: if disconnected during fetch, discard everything
            if (!this.isConnected) {
                this.updateBuffer = null;
                return false;
            }

            let hasState = false;

            if (state && state.byteLength > 0) {
                Y.applyUpdate(this.yDoc, state, this);
                hasState = true;
                this.log.debug('Applied initial state from server', { size: state.byteLength });
            } else {
                this.log.debug('No state exists on server', { documentId: this.documentId });
            }

            this.drainUpdateBuffer();
            return hasState;
        } catch (err) {
            this.log.error('Failed to fetch initial state', err as Error);
            this.drainUpdateBuffer();
            return false;
        }
    }

    /**
     * Wait for initial state sync from server to complete.
     * Returns true if state was received, false if no state.
     *
     * The fetchInitialState callback resolves via initialSyncResolve when complete.
     * Timeout is a safety fallback for network failures only.
     * A `disconnect()` while the sync is still pending resolves it with false.
     *
     * @param timeoutMs - Safety timeout in milliseconds (default: 5000ms)
     * @returns Promise<boolean> - true if state was received, false otherwise
     */
    waitForInitialSync(timeoutMs: number = 5000): Promise<boolean> {
        // If already have state in Y.Doc, resolve immediately
        if (this.yDoc.store.clients.size > 0) {
            return Promise.resolve(true);
        }

        // No fetchInitialState callback → nothing will ever call initialSyncResolve
        if (!this.fetchInitialStateFn) {
            return Promise.resolve(false);
        }

        // If promise already exists, return it
        if (this.initialSyncPromise) {
            return this.initialSyncPromise;
        }

        // Create promise that resolves when state arrives or times out
        this.initialSyncPromise = new Promise<boolean>((resolve) => {
            this.initialSyncResolve = resolve;

            // Timeout - resolve with false if no state arrives
            this.initialSyncTimer = setTimeout(() => {
                this.initialSyncTimer = undefined;
                if (this.initialSyncResolve) {
                    this.log.debug('Initial sync timeout — no state from server');
                    this.initialSyncResolve(false);
                    this.initialSyncResolve = undefined;
                }
            }, timeoutMs);
        });

        return this.initialSyncPromise;
    }

    /**
     * Cancel the initial-sync safety timeout. Called from every path that settles the sync —
     * the early resolve, the timeout itself, and `disconnect()`.
     *
     * Without this the timer outlives the sync it guards: it holds a closure over the provider
     * for the full `timeoutMs` (5s by default) and keeps the Node event loop alive. In tests that
     * is enough to stop a Jest worker from exiting, so the runner force-exits it — which is how
     * this surfaced (`shared-classroom-preview-screen-*` flakiness / worker SIGSEGV under load).
     */
    private clearInitialSyncTimer(): void {
        if (this.initialSyncTimer !== undefined) {
            clearTimeout(this.initialSyncTimer);
            this.initialSyncTimer = undefined;
        }
    }

    /**
     * Disconnect provider and stop syncing
     */
    disconnect(): void {
        if (!this.isConnected) {
            return;
        }

        this.isConnected = false;

        // Clear any pending buffer (prevents applying updates after disconnect)
        this.updateBuffer = null;

        // Settle a pending waitForInitialSync() before dropping it: cancelling the safety timeout
        // removes the only other thing that would have resolved it, so an awaiting caller
        // (LessonYjsService.initialize()) would hang forever on a disconnect mid-sync.
        // Then clear the promise so a future connect() gets a fresh one.
        this.clearInitialSyncTimer();
        this.initialSyncResolve?.(false);
        this.initialSyncResolve = undefined;
        this.initialSyncPromise = undefined;

        // Remove local update handler
        if (this.updateHandler) {
            this.yDoc.off('update', this.updateHandler);
            this.updateHandler = undefined;
        }

        // Remove SignalR listeners (with specific handler references to avoid removing other listeners)
        if (this.receiveUpdateHandler) {
            this.hubConnection.off('ReceiveYjsUpdate', this.receiveUpdateHandler);
            this.receiveUpdateHandler = undefined;
        }

        // Remove awareness handlers (only set up when enableAwareness is true)
        if (this.awarenessUpdateHandler) {
            this._awareness.off('update', this.awarenessUpdateHandler);
            this.awarenessUpdateHandler = undefined;
        }
        if (this.receiveAwarenessUpdateHandler) {
            this.hubConnection.off('ReceiveAwarenessUpdate', this.receiveAwarenessUpdateHandler);
            this.receiveAwarenessUpdateHandler = undefined;
        }

        if (this.enableAwareness) {
            // Notify peers that this client is going offline (awareness auto-cleanup)
            removeAwarenessStates(this._awareness, [this._awareness.clientID], this);
        }

        // Destroy awareness instance to stop its internal 3-second check interval.
        // Without this, the interval runs until yDoc.destroy() — making disconnect()
        // not self-contained in its cleanup.
        this._awareness.destroy();

        // Note: SignalR's onreconnected doesn't have an off() method.
        // The handler checks isConnected internally, so it's safe after disconnect.
        // Clear the reference to avoid holding onto closures.
        this.reconnectedHandler = undefined;

        // Unsubscribe from sync enabled observable
        this.syncEnabledSubscription?.unsubscribe();
        this.syncEnabledSubscription = undefined;
        this.awarenessSendEnabledSubscription?.unsubscribe();
        this.awarenessSendEnabledSubscription = undefined;
    }

    /**
     * Check if provider is connected
     */
    get connected(): boolean {
        return this.isConnected;
    }

    /**
     * Check if sync is currently enabled
     */
    get isSyncEnabled(): boolean {
        return this.syncEnabled;
    }
}
